import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Mic, MicOff, Square, Save, Loader2, CheckCircle, AlertCircle, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUnifiedRecording } from '@/hooks/useUnifiedRecording';

interface UnifiedSessionRecordingProps {
  bookingId: string;
  practitionerId: string;
  clientId: string;
  onTranscriptSaved?: (transcriptId: string) => void;
}

export const UnifiedSessionRecording: React.FC<UnifiedSessionRecordingProps> = ({
  bookingId,
  practitionerId,
  clientId,
  onTranscriptSaved
}) => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [duration, setDuration] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [savedTranscriptId, setSavedTranscriptId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const selectedLanguage = 'en';

  const recording = useUnifiedRecording({
    language: selectedLanguage,
    bookingId,
    practitionerId,
    clientId,
  });

  // Duration timer
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (recording.isRecording) {
      interval = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [recording.isRecording]);

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const saveTranscript = useCallback(async () => {
    const finalTranscript = recording.transcript;
    if (!finalTranscript?.trim()) {
      toast({
        title: "无法保存",
        description: "没有录音内容可以保存",
        variant: "destructive"
      });
      return;
    }

    setSaveStatus('saving');
    setIsSaving(true);
    
    try {
      const { data, error } = await supabase
        .from('session_transcripts')
        .insert({
          booking_id: bookingId,
          practitioner_id: practitionerId,
          client_id: clientId,
          transcript_text: finalTranscript,
          recording_duration_seconds: duration,
          status: 'draft'
        })
        .select()
        .single();

      if (error) throw error;

      setSavedTranscriptId(data.id);
      setSaveStatus('success');
      onTranscriptSaved?.(data.id);
      
      toast({
        title: "保存成功",
        description: `转录文本已成功保存`,
        duration: 5000,
      });

      await processWithAI(data.id, finalTranscript);
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('error');
      toast({
        title: "Save Failed",
        description: error instanceof Error ? error.message : "An error occurred while saving transcript",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [recording.transcript, duration, bookingId, practitionerId, clientId, onTranscriptSaved, toast]);

  const processWithAI = async (transcriptId: string, transcriptText: string) => {
    setIsProcessingAI(true);
    try {
      const { error } = await supabase.functions.invoke('process-session-transcript', {
        body: { transcriptId, transcriptText }
      });
      if (error) throw error;
      toast({ title: "AI 分析完成", description: "医疗记录已生成", duration: 3000 });
    } catch (error) {
      console.error('AI processing failed:', error);
      toast({
        title: "AI 分析失败",
        description: "转录文本已保存，但 AI 分析失败",
        variant: "destructive"
      });
    } finally {
      setIsProcessingAI(false);
    }
  };

  const handleStartRecording = () => {
    setDuration(0);
    setSavedTranscriptId(null);
    setSaveStatus('idle');
    recording.startRecording();
  };

  const handleStopRecording = () => {
    recording.stopRecording();
  };

  const handleReset = () => {
    recording.resetTranscript();
    setDuration(0);
    setSavedTranscriptId(null);
    setSaveStatus('idle');
  };

  const handleResetSession = () => {
    recording.resetSession();
    setDuration(0);
    setSavedTranscriptId(null);
    setSaveStatus('idle');
  };

  if (!recording.isSupported) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MicOff className="h-5 w-5" />
            录音功能不可用
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">此浏览器不支持录音功能，请使用现代浏览器。</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`w-full mx-auto ${isMobile ? 'max-w-full' : 'max-w-4xl'}`}>
      <CardHeader className={isMobile ? 'pb-3' : ''}>
        <CardTitle className={`flex items-center ${isMobile ? 'flex-col gap-3' : 'justify-between'}`}>
          <div className="flex items-center gap-2">
            <Mic className="h-5 w-5" />
            <span className={isMobile ? 'text-lg' : ''}>
              {selectedLanguage.startsWith('zh') ? '语音识别录音' : 'Speech Recognition Recording'}
            </span>
          </div>
          <div className={`flex items-center gap-2 ${isMobile ? 'flex-wrap justify-center' : ''}`}>
            <Badge variant={recording.isRecording ? "destructive" : "secondary"} className={isMobile ? 'text-xs' : ''}>
              {recording.isRecording ? 'Recording' : 'Stopped'}
            </Badge>
            <Badge variant="outline" className={isMobile ? 'text-xs font-mono' : ''}>
              {formatDuration(duration)}
            </Badge>
            <Badge variant="outline" className={isMobile ? 'text-xs' : ''}>
              Web Speech
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Recording controls */}
        <div className={`flex items-center gap-2 ${isMobile ? 'flex-col space-y-2' : 'flex-wrap'}`}>
          <div className={`flex items-center gap-2 ${isMobile ? 'w-full' : ''}`}>
            {!recording.isRecording ? (
              <Button
                onClick={handleStartRecording}
                className={`flex items-center gap-2 ${isMobile ? 'flex-1 min-h-[48px]' : ''}`}
                disabled={isSaving || isProcessingAI}
              >
                <Mic className="h-4 w-4" />
                {selectedLanguage.startsWith('zh') ? '开始录音' : 'Start Recording'}
              </Button>
            ) : (
              <Button
                onClick={handleStopRecording}
                variant="destructive"
                className={`flex items-center gap-2 ${isMobile ? 'flex-1 min-h-[48px]' : ''}`}
              >
                <Square className="h-4 w-4" />
                {selectedLanguage.startsWith('zh') ? '停止录音' : 'Stop Recording'}
              </Button>
            )}
            
            <Button
              onClick={saveTranscript}
              disabled={!recording.transcript?.trim() || recording.isRecording || isSaving || isProcessingAI}
              className={`flex items-center gap-2 ${isMobile ? 'flex-1 min-h-[48px]' : ''}`}
              variant={saveStatus === 'success' ? 'default' : 'outline'}
            >
              {saveStatus === 'saving' ? <Loader2 className="h-4 w-4 animate-spin" /> :
               saveStatus === 'success' ? <CheckCircle className="h-4 w-4" /> :
               saveStatus === 'error' ? <AlertCircle className="h-4 w-4" /> :
               <Save className="h-4 w-4" />}
              {saveStatus === 'saving' ? 'Saving...' : 
               saveStatus === 'success' ? 'Saved' :
               saveStatus === 'error' ? 'Save Failed' : 'Save Transcript'}
            </Button>
          </div>
          
          <div className={`flex items-center gap-2 ${isMobile ? 'w-full' : ''}`}>
            <Button 
              onClick={handleReset}
              variant="outline"
              disabled={recording.isRecording || isSaving || isProcessingAI}
              className={isMobile ? 'flex-1 min-h-[44px]' : ''}
            >
              Reset Transcript
            </Button>
            <Button 
              onClick={handleResetSession}
              variant="outline"
              disabled={recording.isRecording || isSaving || isProcessingAI}
              className={`flex items-center gap-2 ${isMobile ? 'flex-1 min-h-[44px]' : ''}`}
            >
              <RotateCcw className="h-4 w-4" />
              Reset Session
            </Button>
          </div>
        </div>

        {/* Status messages */}
        {recording.error && (
          <div className={`p-3 bg-destructive/10 border border-destructive/20 rounded-lg ${isMobile ? 'text-sm' : ''}`}>
            <p className="text-destructive">Error: {recording.error}</p>
          </div>
        )}

        {recording.isBatchProcessing && (
          <div className={`p-3 bg-blue-50 border border-blue-200 rounded-lg ${isMobile ? 'text-sm' : ''}`}>
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <div>
                <p className="text-blue-700 font-medium">
                  {selectedLanguage.startsWith('zh') ? '正在生成精确转录...' : 'Generating precise transcription...'}
                </p>
                <p className="text-xs text-blue-600 mt-0.5">
                  {selectedLanguage.startsWith('zh') ? 'AssemblyAI 批量处理中，预计 30 秒 - 2 分钟' : 'AssemblyAI batch processing, ~30s - 2min'}
                </p>
              </div>
            </div>
          </div>
        )}

        {isProcessingAI && (
          <div className={`p-3 bg-blue-50 border border-blue-200 rounded-lg ${isMobile ? 'text-sm' : ''}`}>
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <p className="text-blue-700">AI 分析中...</p>
            </div>
          </div>
        )}

        {saveStatus === 'success' && savedTranscriptId && (
          <div className={`p-3 bg-green-50 border border-green-200 rounded-lg ${isMobile ? 'text-sm' : ''}`}>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <p className="text-green-700">Transcript saved successfully</p>
            </div>
          </div>
        )}

        <Separator />

        {/* Transcription display */}
        <div className="space-y-2">
          <h3 className="font-medium">
            {recording.batchTranscript 
              ? (selectedLanguage.startsWith('zh') ? '精确转录（含说话人识别）' : 'Precise Transcription (with speaker ID)')
              : (selectedLanguage.startsWith('zh') ? '实时预览（仅供参考）' : 'Live Preview (for reference only)')
            }
          </h3>
          
          <ScrollArea className={`w-full border rounded-lg p-3 ${isMobile ? 'h-48' : 'h-64'}`}>
            <div className="space-y-3">
              {recording.batchTranscript ? (
                <div className="text-sm">
                  <div className="mt-1 p-2 bg-green-50 rounded border-l-4 border-green-500">
                    <pre className="whitespace-pre-wrap text-foreground font-mono text-xs">
                      {recording.batchTranscript}
                    </pre>
                  </div>
                </div>
              ) : recording.previewTranscript ? (
                <div className="text-sm">
                  <div className="mt-1 p-2 bg-muted/50 rounded border-l-4 border-muted-foreground/30">
                    <p className="text-foreground text-xs">
                      {recording.previewTranscript}
                      {recording.interimTranscript && (
                        <span className="text-muted-foreground italic"> {recording.interimTranscript}</span>
                      )}
                    </p>
                  </div>
                </div>
              ) : (
                <p className={`text-muted-foreground text-center ${isMobile ? 'py-6 text-sm' : 'py-8'}`}>
                  {recording.isRecording 
                    ? "Listening for speech..."
                    : "Click start recording to begin speech recognition"
                  }
                </p>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Stats */}
        {recording.transcript && (
          <div className={`flex items-center text-muted-foreground ${isMobile ? 'flex-col gap-1 text-xs' : 'gap-4 text-sm'}`}>
            <span>Characters: {recording.transcript.length}</span>
            <span>Engine: {recording.batchTranscript ? 'AssemblyAI Batch' : 'Web Speech'}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
